import { useEffect, useState } from 'react';

import { supabase } from '../../supabase';
import { useVencimientos } from './useVencimientos';
import Paginacion from '../../components/Paginacion';
import ResponsiveFilterCard from '../../components/ResponsiveFilterCard';
import ActiveFiltersLegend from '../../components/ActiveFiltersLegend';

import {
  Table,
  Form,
  Button,
  Container,
  Card,
  Row,
  Col,
  Alert,
  Modal,
  Spinner,
} from 'react-bootstrap';

import { Pencil, Search, XCircle } from 'react-bootstrap-icons';
import dayjs from 'dayjs';
import { esFechaISOValida, validarImporte } from '../../utils/vencimientosValidation';

const PAGE_SIZE_VENCIMIENTOS = 50;
const countFormatter = new Intl.NumberFormat('es-AR');
const EMPTY_DUE_FILTERS = {
  fechaDesde: '',
  fechaHasta: '',
  estado: '',
  texto: '',
  titular: '',
  tipo_servicio: '',
};

const titulares = [
  'FERRERO/FINETTI',
  'DELYAR SA',
  'DIST. DEL ESTE',
  'DEV RANDOM',
  'FERRERO',
  'FINETTI',
  'DON HECTOR',
  'DOANET',
  'MIRTA DEL VALLE BRAVO',
  'DESARROLLOS FORESTALES',
];

const tiposServicio = [
  'Rentas',
  'Inmobiliario Municipal',
  'Servicio',
  'Telecomunicaciones',
  'Automotor',
  'API',
  'Inmobiliario Provincial',
  'Tasas Rurales',
];

export default function VencimientosPage() {
  const [filtros, setFiltros] = useState(EMPTY_DUE_FILTERS);
  const [filtrosBorrador, setFiltrosBorrador] = useState(EMPTY_DUE_FILTERS);

  const [modal, setModal] = useState({ show: false, vencimiento: null });
  const [editingError, setEditingError] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [modalVer, setModalVer] = useState({ show: false, servicio: null });
  const [modalCampo, setModalCampo] = useState({
    show: false,
    vencimiento: null,
    campo: null,
  });

  const añoActual = dayjs().year();
  const [anioActivo, setAnioActivo] = useState(añoActual);
  const [paginaVencimientos, setPaginaVencimientos] = useState(1);

  const {
    vencimientos,
    total,
    totalAnio,
    years,
    refetch,
    isLoading,
    isMetaLoading,
    error,
  } = useVencimientos({
    filtros,
    anio: anioActivo,
    page: paginaVencimientos,
    pageSize: PAGE_SIZE_VENCIMIENTOS,
  });

  const filtrosActivos = Object.values(filtrosBorrador).filter((valor) =>
    String(valor || '').trim()
  ).length;

  const estadoFilterLabels = {
    pagado: 'Pagado',
    vencido: 'Vencido',
    por_vencer: 'Por Vencer',
    pendiente: 'Pendiente',
  };

  const activeFilterItems = [
    filtros.fechaDesde && { label: 'Desde', value: filtros.fechaDesde },
    filtros.fechaHasta && { label: 'Hasta', value: filtros.fechaHasta },
    filtros.estado && { label: 'Estado', value: estadoFilterLabels[filtros.estado] || filtros.estado },
    filtros.texto && { label: 'Buscar', value: filtros.texto },
    filtros.titular && { label: 'Titular', value: filtros.titular },
    filtros.tipo_servicio && { label: 'Tipo', value: filtros.tipo_servicio },
  ].filter(Boolean);
  const rangoInvalido =
    filtrosBorrador.fechaDesde &&
    filtrosBorrador.fechaHasta &&
    filtrosBorrador.fechaDesde > filtrosBorrador.fechaHasta;
  const anioActivoDisponible = years.some(
    ({ year }) => Number(year) === Number(anioActivo)
  );
  const isResultsLoading =
    isLoading || (!isMetaLoading && years.length > 0 && !anioActivoDisponible);

  useEffect(() => {
    setPaginaVencimientos(1);
  }, [filtros]);

  useEffect(() => {
    if (isMetaLoading || years.length === 0) return;

    const anioDisponible = years.some(
      ({ year }) => Number(year) === Number(anioActivo)
    );

    if (!anioDisponible) {
      setAnioActivo(years[0].year);
      setPaginaVencimientos(1);
    }
  }, [anioActivo, isMetaLoading, years]);

  const abrirEdicionCampo = (venc, campo) => {
    setEditingError('');
    setModalCampo({ show: true, vencimiento: { ...venc }, campo });
  };

  const getRowClass = (v) => {
    if (v.pagado) return 'vencimientos-row is-paid';

    const hoy = dayjs();
    const fecha = dayjs(v.fecha_vencimiento);

    if (fecha.isBefore(hoy, 'day')) return 'vencimientos-row is-overdue';
    if (fecha.diff(hoy, 'day') <= 7) return 'vencimientos-row is-warning';

    return 'vencimientos-row is-pending';
  };

  const getEstadoTexto = (v) => {
    if (v.pagado) return 'Pagado';

    const hoy = dayjs();
    const fecha = dayjs(v.fecha_vencimiento);

    if (fecha.isBefore(hoy, 'day')) return 'Vencido';
    if (fecha.diff(hoy, 'day') <= 7) return 'Por Vencer';

    return 'Pendiente';
  };

  const getEstadoBadgeClass = (v) => {
    const estado = getEstadoTexto(v);

    if (estado === 'Pagado') return 'is-paid';
    if (estado === 'Vencido') return 'is-overdue';
    if (estado === 'Por Vencer') return 'is-warning';

    return 'is-pending';
  };

  const handleLimpiar = () => {
    setPaginaVencimientos(1);
    setFiltrosBorrador({ ...EMPTY_DUE_FILTERS });
    setFiltros({ ...EMPTY_DUE_FILTERS });
  };

  const aplicarFiltros = () => {
    if (rangoInvalido) return;
    setPaginaVencimientos(1);
    setFiltros({ ...filtrosBorrador });
  };

  const abrirEdicion = (venc) => {
    setEditingError('');
    setModal({ show: true, vencimiento: { ...venc }, originalPagado: Boolean(venc.pagado) });
  };

  const cerrarModal = () => {
    setModal({ show: false, vencimiento: null });
  };

  const guardarCambios = async () => {
    if (!modal.vencimiento || isSaving) return;
    const { id, importe_pagado, observaciones, pagado, fecha_pago } = modal.vencimiento;
    const importe = validarImporte(importe_pagado);
    if (!importe.ok) {
      setEditingError('El importe pagado debe ser un número igual o mayor a cero.');
      return;
    }

    // Conservar la fecha original cuando ya estaba pagado: editar observaciones
    // o el importe no debe cambiar la fecha histórica de pago.
    const update = {
      observaciones: observaciones || null,
      importe_pagado: importe.value,
      pagado,
      fecha_pago: pagado
        ? (fecha_pago || (modal.originalPagado ? null : dayjs().format('YYYY-MM-DD')))
        : null,
    };

    setIsSaving(true);
    setEditingError('');
    try {
      const { error } = await supabase.from('vencimientos').update(update).eq('id', id);
      if (error) throw error;
      cerrarModal();
      void refetch().catch(() => {}); // SWR expone el error de consulta en la página.
    } catch (error) {
      setEditingError(`No se pudo guardar: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const totalPaginas = Math.max(1, Math.ceil(totalAnio / PAGE_SIZE_VENCIMIENTOS));
  const primerResultadoAnio = totalAnio
    ? (paginaVencimientos - 1) * PAGE_SIZE_VENCIMIENTOS + 1
    : 0;
  const ultimoResultadoAnio = totalAnio
    ? Math.min(paginaVencimientos * PAGE_SIZE_VENCIMIENTOS, totalAnio)
    : 0;

  useEffect(() => {
    if (!isMetaLoading && totalAnio > 0 && paginaVencimientos > totalPaginas) {
      setPaginaVencimientos(totalPaginas);
    }
  }, [isMetaLoading, paginaVencimientos, totalAnio, totalPaginas]);

  const renderListado = () => (
    <>
      {totalPaginas > 1 && (
        <div className='crm-table-pagination vencimientos-pagination is-top'>
          <Paginacion
            pagina={paginaVencimientos}
            totalPaginas={totalPaginas}
            onChange={setPaginaVencimientos}
            disabled={isResultsLoading}
          />
        </div>
      )}
      <div className='d-none d-md-block table-responsive'>
        <Table
          striped
          hover
          responsive
          size='sm'
          className='vencimientos-table align-middle text-center mb-0'
        >
          <thead className='table-light'>
            <tr className='text-center text-nowrap'>
              <th>Fecha</th>
              <th>Detalle</th>
              <th>Proveedor</th>
              <th>Referencia</th>
              <th>Forma de Pago</th>
              <th>Importe Estimado</th>
              <th>Estado</th>
              <th>Acciones</th>
            </tr>
          </thead>

          <tbody>
            {vencimientos.map((v) => (
              <tr key={v.id} className={getRowClass(v)}>
                <td className='text-center align-middle'>
                  <span className='d-inline-flex align-items-center gap-1'>
                    {dayjs(v.fecha_vencimiento).format('DD/MM/YYYY')}

                    <Button
                      variant='link'
                      size='sm'
                      className='p-0 vencimientos-edit-link'
                      title='Editar Fecha'
                      onClick={() => abrirEdicionCampo(v, 'fecha')}
                    >
                      <Pencil />
                    </Button>
                  </span>
                </td>

                <td className='text-start align-middle'>
                  {v.servicios?.tipo_servicio} - {v.servicios?.detalle}
                </td>

                <td className='text-center align-middle'>
                  {v.servicios?.empresa_proveedora}
                </td>

                <td className='text-start align-middle'>
                  {v.servicios?.referencia_cuenta || '—'}
                </td>

                <td className='text-center align-middle'>
                  {v.servicios?.forma_pago || '—'}
                </td>

                <td className='text-center align-middle'>
                  <span className='d-inline-flex align-items-center gap-1'>
                      ${Number(v?.importe || 0).toFixed(2)}

                    <Button
                      variant='link'
                      size='sm'
                      className='p-0 vencimientos-edit-link'
                      title='Editar Importe'
                      onClick={() => abrirEdicionCampo(v, 'importe')}
                    >
                      <Pencil />
                    </Button>
                  </span>
                </td>

                <td className='text-center align-middle'>
                  <span className={`crm-status-badge ${getEstadoBadgeClass(v)}`}>
                    {getEstadoTexto(v)}
                  </span>
                </td>

                <td className='text-center align-middle'>
                  <div className='vencimientos-acciones'>
                    <Button
                      size='sm'
                      variant='outline-secondary'
                      onClick={() =>
                        setModalVer({ show: true, servicio: v.servicios })
                      }
                    >
                        Ver
                    </Button>

                    <Button
                      size='sm'
                      variant='outline-primary'
                      onClick={() => abrirEdicion(v)}
                    >
                        Editar
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </div>

      <div className='vencimientos-mobile-list d-md-none'>
        {vencimientos.map((v) => (
          <Card key={v.id} className='vencimientos-mobile-card'>
            <Card.Body>
              <div className='vencimientos-mobile-card-header'>
                <div>
                  <h6 className='vencimientos-mobile-title'>
                    {v.servicios?.tipo_servicio || 'Sin servicio'}
                  </h6>

                  <p className='vencimientos-mobile-subtitle mb-0'>
                    {v.servicios?.empresa_proveedora || 'Sin proveedor'}
                  </p>
                </div>

                <span className={`crm-status-badge ${getEstadoBadgeClass(v)}`}>
                  {getEstadoTexto(v)}
                </span>
              </div>

              <div className='vencimientos-mobile-info'>
                <div>
                  <span>Fecha</span>
                  <strong>
                    {dayjs(v.fecha_vencimiento).format('DD/MM/YYYY')}
                  </strong>
                </div>

                <div>
                  <span>Detalle</span>
                  <strong>{v.servicios?.detalle || '—'}</strong>
                </div>

                <div>
                  <span>Referencia</span>
                  <strong>{v.servicios?.referencia_cuenta || '—'}</strong>
                </div>

                <div>
                  <span>Forma de pago</span>
                  <strong>{v.servicios?.forma_pago || '—'}</strong>
                </div>

                <div>
                  <span>Importe estimado</span>
                  <strong>${Number(v?.importe || 0).toFixed(2)}</strong>
                </div>
              </div>

              <div className='vencimientos-mobile-actions'>
                <Button
                  size='sm'
                  variant='outline-secondary'
                  onClick={() =>
                    setModalVer({ show: true, servicio: v.servicios })
                  }
                >
                    Ver Servicio
                </Button>

                <Button
                  size='sm'
                  variant='outline-primary'
                  onClick={() => abrirEdicion(v)}
                >
                    Editar Pago
                </Button>

                <div className='vencimientos-mobile-inline-actions'>
                  <Button
                    size='sm'
                    variant='outline-primary'
                    onClick={() => abrirEdicionCampo(v, 'fecha')}
                  >
                    <Pencil className='me-1' />
                      Fecha
                  </Button>

                  <Button
                    size='sm'
                    variant='outline-primary'
                    onClick={() => abrirEdicionCampo(v, 'importe')}
                  >
                    <Pencil className='me-1' />
                      Importe
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>


      {totalPaginas > 1 && (
        <div className='crm-table-pagination vencimientos-pagination is-bottom'>
          <Paginacion
            pagina={paginaVencimientos}
            totalPaginas={totalPaginas}
            onChange={setPaginaVencimientos}
            disabled={isResultsLoading}
          />
        </div>
      )}
    </>
  );


  return (
    <Container fluid className='vencimientos-wrapper'>
      <header className='crm-page-header'>
        <div className='crm-page-heading'>
          <h1 className='crm-page-title'>Listado de Vencimientos</h1>
          <p className='crm-page-subtitle'>
            Consultá, filtrá y actualizá los vencimientos de todos los servicios.
          </p>
        </div>
      </header>

      <ResponsiveFilterCard
        className='vencimientos-filter-card'
        title='Filtros de vencimientos'
        activeCount={filtrosActivos}
      >
        <div className='crm-filter-heading'>
          <span>Consulta</span>
          <h2>Filtros de búsqueda</h2>
        </div>

        <Row className='vencimientos-filter-grid align-items-end g-2'>
          <Col className='vencimientos-filter-field'>
            <Form.Label>Desde</Form.Label>
            <Form.Control
              type='date'
              value={filtrosBorrador.fechaDesde}
              isInvalid={rangoInvalido}
              onChange={(e) =>
                setFiltrosBorrador({
                  ...filtrosBorrador,
                  fechaDesde: e.target.value,
                })
              }
            />
          </Col>

          <Col className='vencimientos-filter-field'>
            <Form.Label>Hasta</Form.Label>
            <Form.Control
              type='date'
              value={filtrosBorrador.fechaHasta}
              isInvalid={rangoInvalido}
              onChange={(e) =>
                setFiltrosBorrador({
                  ...filtrosBorrador,
                  fechaHasta: e.target.value,
                })
              }
            />
          </Col>

          <Col className='vencimientos-filter-field'>
            <Form.Label>Estado</Form.Label>
            <Form.Select
              value={filtrosBorrador.estado}
              onChange={(e) =>
                setFiltrosBorrador({
                  ...filtrosBorrador,
                  estado: e.target.value,
                })
              }
            >
              <option value=''>Todos</option>
              <option value='pagado'>Pagado</option>
              <option value='vencido'>Vencido</option>
              <option value='por_vencer'>Por Vencer</option>
              <option value='pendiente'>Pendiente</option>
            </Form.Select>
          </Col>

          <Col className='vencimientos-filter-field is-search'>
            <Form.Label>Buscar</Form.Label>
            <Form.Control
              type='text'
              placeholder='Proveedor, Detalle, Servicio...'
              value={filtrosBorrador.texto}
              onChange={(e) =>
                setFiltrosBorrador({
                  ...filtrosBorrador,
                  texto: e.target.value,
                })
              }
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !rangoInvalido) {
                  aplicarFiltros();
                }
              }}
            />
          </Col>

          <Col className='vencimientos-filter-field'>
            <Form.Label>Titular</Form.Label>
            <Form.Select
              value={filtrosBorrador.titular}
              onChange={(e) =>
                setFiltrosBorrador({
                  ...filtrosBorrador,
                  titular: e.target.value,
                })
              }
            >
              <option value=''>Todos</option>
              {titulares.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Form.Select>
          </Col>

          <Col className='vencimientos-filter-field'>
            <Form.Label>Tipo de Servicio</Form.Label>
            <Form.Select
              value={filtrosBorrador.tipo_servicio}
              onChange={(e) =>
                setFiltrosBorrador({
                  ...filtrosBorrador,
                  tipo_servicio: e.target.value,
                })
              }
            >
              <option value=''>Todos</option>
              {tiposServicio.map((t) => (
                <option key={t} value={t}>
                  {t}
                </option>
              ))}
            </Form.Select>
          </Col>

        </Row>
        {rangoInvalido && (
          <p className='crm-filter-error'>
            La fecha Desde no puede ser posterior a Hasta.
          </p>
        )}

        <div className='crm-filter-actions-row'>
          <Button variant='outline-primary' onClick={handleLimpiar}>
            <XCircle aria-hidden='true' />
            Limpiar filtros
          </Button>
          <Button
            variant='primary'
            disabled={rangoInvalido}
            onClick={aplicarFiltros}
          >
            <Search aria-hidden='true' />
            Aplicar filtros
          </Button>
        </div>
      </ResponsiveFilterCard>

      <ActiveFiltersLegend items={activeFilterItems} />

      <Card className='vencimientos-table-card'>
        <div className='crm-results-header vencimientos-results-header'>
          <div>
            <span>Resultados</span>
            <h2>Vencimientos encontrados</h2>
            <p>
              {isMetaLoading
                ? 'Calculando cantidad real de vencimientos...'
                : `${countFormatter.format(total)} resultado${total === 1 ? '' : 's'} con los filtros activos.`}
            </p>
          </div>

          {!isMetaLoading && totalAnio > 0 && (
            <div className='vencimientos-results-meta' aria-live='polite'>
              <strong>
                Página {paginaVencimientos} de {totalPaginas}
              </strong>
              <span>
                {countFormatter.format(primerResultadoAnio)}–{countFormatter.format(ultimoResultadoAnio)} de{' '}
                {countFormatter.format(totalAnio)} en {anioActivo}
              </span>
              <small>{PAGE_SIZE_VENCIMIENTOS} por página</small>
            </div>
          )}
        </div>

        {!isMetaLoading && years.length > 0 && (
          <nav className='crm-subtabs vencimientos-year-tabs' aria-label='Año de vencimientos'>
            {years.map(({ year }) => (
              <button
                key={year}
                type='button'
                className={Number(anioActivo) === Number(year) ? 'is-active' : ''}
                aria-current={Number(anioActivo) === Number(year) ? 'page' : undefined}
                onClick={() => {
                  setAnioActivo(year);
                  setPaginaVencimientos(1);
                }}
              >
                {year}
              </button>
            ))}
          </nav>
        )}

        {error ? (
          <Alert variant='danger' className='vencimientos-error-state'>
            No fue posible cargar los vencimientos. Volvé a intentarlo en unos
            minutos.
          </Alert>
        ) : isResultsLoading ? (
          <div className='crm-loading-state vencimientos-loading-state'>
            <Spinner animation='border' variant='primary' />
            <span>Cargando vencimientos...</span>
          </div>
        ) : total === 0 ? (
          <div className='crm-table-empty vencimientos-empty-state'>
            No se encontraron vencimientos para los filtros seleccionados.
          </div>
        ) : totalAnio === 0 ? (
          <div className='crm-table-empty vencimientos-empty-state'>
            No se encontraron vencimientos para el año seleccionado.
          </div>
        ) : (
          renderListado()
        )}
      </Card>

      <Modal show={modal.show} onHide={cerrarModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Editar Vencimiento</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editingError && <Alert variant='danger' role='alert'>{editingError}</Alert>}
          {modal.vencimiento && (
            <>
              <div className='vencimientos-modal-summary'>
                <div>
                  <span>Servicio</span>
                  <strong>
                    {modal.vencimiento.servicios?.tipo_servicio || '—'}
                  </strong>
                </div>
                <div>
                  <span>Proveedor</span>
                  <strong>
                    {modal.vencimiento.servicios?.empresa_proveedora || '—'}
                  </strong>
                </div>
              </div>

              <Form.Group className='mb-3'>
                <Form.Label>Importe Pagado</Form.Label>

                <Form.Control
                  type='number'
                  min={0}
                  value={modal.vencimiento.importe_pagado ?? ''}
                  onChange={(e) =>
                    setModal((prev) => ({
                      ...prev,
                      vencimiento: {
                        ...prev.vencimiento,
                        importe_pagado: e.target.value,
                      },
                    }))
                  }
                />
              </Form.Group>

              <Form.Group className='mb-3'>
                <Form.Label>Observaciones</Form.Label>

                <Form.Control
                  as='textarea'
                  rows={3}
                  value={modal.vencimiento.observaciones || ''}
                  onChange={(e) =>
                    setModal((prev) => ({
                      ...prev,
                      vencimiento: {
                        ...prev.vencimiento,
                        observaciones: e.target.value,
                      },
                    }))
                  }
                />
              </Form.Group>

              <Form.Check
                type='checkbox'
                label='Marcar como pagado'
                checked={modal.vencimiento.pagado}
                onChange={(e) =>
                  setModal((prev) => ({
                    ...prev,
                    vencimiento: {
                      ...prev.vencimiento,
                      pagado: e.target.checked,
                    },
                  }))
                }
              />
            </>
          )}
        </Modal.Body>

        <Modal.Footer className='flex-column flex-sm-row'>
          <Button variant='secondary' disabled={isSaving} onClick={cerrarModal}>
            Cancelar
          </Button>

          <Button variant='primary' disabled={isSaving} onClick={guardarCambios}>
            {isSaving ? 'Guardando...' : 'Guardar'}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={modalVer.show}
        onHide={() => setModalVer({ show: false, servicio: null })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Datos del Servicio</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {modalVer.servicio && (
            <div className='vencimientos-detail-grid'>
              {[
                ['Titular', modalVer.servicio.titular],
                ['Tipo', modalVer.servicio.tipo_servicio],
                ['Proveedor', modalVer.servicio.empresa_proveedora],
                ['Detalle', modalVer.servicio.detalle],
                ['Forma de pago', modalVer.servicio.forma_pago],
                ['Referencia / Cuenta', modalVer.servicio.referencia_cuenta],
                ['Periodicidad', modalVer.servicio.periodicidad],
                [
                  'Cantidad de vencimientos',
                  modalVer.servicio.cantidad_vencimientos,
                ],
                ['Observaciones', modalVer.servicio.observaciones],
              ].map(([etiqueta, valor]) => (
                <div
                  key={etiqueta}
                  className={`vencimientos-detail-item ${
                    etiqueta === 'Observaciones' ? 'is-wide' : ''
                  }`}
                >
                  <span>{etiqueta}</span>
                  <strong>{valor || '—'}</strong>
                </div>
              ))}
            </div>
          )}
        </Modal.Body>

        <Modal.Footer className='flex-column flex-sm-row'>
          <Button
            variant='secondary'
            onClick={() => setModalVer({ show: false, servicio: null })}
          >
            Cerrar
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={modalCampo.show}
        onHide={() =>
          setModalCampo({ show: false, vencimiento: null, campo: null })
        }
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Editar{' '}
            {modalCampo.campo === 'fecha'
              ? 'Fecha de Vencimiento'
              : 'Importe Estimado'}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {editingError && <Alert variant='danger' role='alert'>{editingError}</Alert>}
          {modalCampo.vencimiento && (
            <Form.Group>
              <Form.Label>
                {modalCampo.campo === 'fecha'
                  ? 'Nueva Fecha'
                  : 'Nuevo Importe'}
              </Form.Label>

              <Form.Control
                type={modalCampo.campo === 'fecha' ? 'date' : 'number'}
                min={modalCampo.campo === 'importe' ? 0 : undefined}
                value={
                  modalCampo.campo === 'fecha'
                    ? modalCampo.vencimiento.fecha_vencimiento || ''
                    : modalCampo.vencimiento.importe || ''
                }
                onChange={(e) => {
                  const value = e.target.value;

                  setModalCampo((prev) => ({
                    ...prev,
                    vencimiento: {
                      ...prev.vencimiento,
                      [modalCampo.campo === 'fecha'
                        ? 'fecha_vencimiento'
                        : 'importe']: value,
                    },
                  }));
                }}
                onWheel={(e) => e.target.blur()}
              />
            </Form.Group>
          )}
        </Modal.Body>

        <Modal.Footer className='flex-column flex-sm-row'>
          <Button
            variant='secondary'
            disabled={isSaving}
            onClick={() =>
              setModalCampo({ show: false, vencimiento: null, campo: null })
            }
          >
            Cancelar
          </Button>

          <Button
            variant='primary'
            disabled={isSaving}
            onClick={async () => {
              if (!modalCampo.vencimiento || isSaving) return;
              const { id, fecha_vencimiento, importe } = modalCampo.vencimiento;
              let update;
              if (modalCampo.campo === 'fecha') {
                if (!esFechaISOValida(fecha_vencimiento)) {
                  setEditingError('Ingresá una fecha de vencimiento válida.');
                  return;
                }
                update = {
                  fecha_vencimiento,
                  año: Number(fecha_vencimiento.slice(0, 4)),
                };
              } else {
                const importeValidado = validarImporte(importe, { obligatorio: true });
                if (!importeValidado.ok) {
                  setEditingError('El importe debe ser un número igual o mayor a cero.');
                  return;
                }
                update = { importe: importeValidado.value };
              }
              setEditingError('');
              setIsSaving(true);
              try {
                const { error } = await supabase.from('vencimientos').update(update).eq('id', id);
                if (error) throw error;
                setModalCampo({ show: false, vencimiento: null, campo: null });
                void refetch().catch(() => {});
              } catch (error) {
                setEditingError(`No se pudo guardar: ${error.message}`);
              } finally {
                setIsSaving(false);
              }
            }}
          >
            Guardar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}
